import { supabase } from '../supabase';
import { 
  withTransaction, 
  validateStockOperation, 
  bulkInsert, 
  bulkUpdate, 
  bulkDelete, 
  QueryOptimizer,
  type TransactionContext 
} from '../utils/database';
import type { ProductZoneStorage } from '../stores/warehouseZoneStore';
import {
  createWarehouseError,
  ZoneNotFoundError,
  StorageRecordNotFoundError,
  InvalidQuantityError,
  ZoneInactiveError,
  ZoneCapacityExceededError,
  UnauthorizedOperationError
} from '../errors/warehouseErrors';
import { StockReservationService } from './stockReservationService';
import { AuditTrailService, type AuditContext } from './auditTrailService';
import { formatWarehouseName } from '../utils/warehouseLabel';

export interface WarehouseOperation {
  productId: string;
  zoneId: string;
  quantity: number;
  operation: 'stock' | 'destock';
  notes?: string;
  userId: string;
  organizationId: string;
}

interface StorageRecordWithRelations {
  zone_id: string;
  product_id: string;
  quantity: number;
  product?: {
    name: string;
  };
  zone?: {
    name: string;
  };
}

interface ProductZoneStorageInsert {
  organization_id: string;
  product_id: string;
  zone_id: string;
  quantity: number;
  notes?: string;
  stored_by: string;
  stored_at?: string;
}

interface WarehouseZoneWithUsage {
  id: string;
  organization_id: string;
  name: string;
  code: string;
  description?: string;
  location?: string;
  capacity_limit?: number;
  current_usage: number;
  is_active: boolean;
  created_at: string;
  product_count?: number;
  total_items?: number;
  usage_percentage?: number;
}

export class WarehouseService {
  /**
   * Applica la variazione (positiva o negativa) al totale del prodotto.
   * Il totale non si ricalcola dalle zone: i pezzi non stoccati in nessuna zona resterebbero fuori dal conto.
   */
  private static async applyProductStockDelta(
    productId: string,
    delta: number,
    context: TransactionContext
  ): Promise<void> {
    if (delta === 0) return;

    const { data: current, error: readError } = await supabase
      .from('products')
      .select('stock_quantity')
      .eq('id', productId)
      .eq('organization_id', context.organizationId)
      .single();

    if (readError) {
      console.error('Error reading product stock quantity:', readError);
      throw createWarehouseError(readError, 'read product stock quantity');
    }

    const totalQuantity = Math.max(0, (current?.stock_quantity || 0) + delta);

    const { error } = await supabase
      .from('products')
      .update({
        stock_quantity: totalQuantity,
        stock_status: totalQuantity > 0 ? 'instock' : 'outofstock'
      })
      .eq('id', productId)
      .eq('organization_id', context.organizationId);

    if (error) {
      console.error('Error updating product stock quantity:', error);
      throw createWarehouseError(error, 'update product stock quantity');
    }
  }

  /** Pezzi contati nel totale del prodotto ma non stoccati in nessuna zona. */
  private static async getUnassignedStock(
    productId: string,
    context: TransactionContext
  ): Promise<number> {
    const [{ data: product }, { data: zoneRows }] = await Promise.all([
      supabase.from('products').select('stock_quantity').eq('id', productId).eq('organization_id', context.organizationId).single(),
      supabase.from('product_zone_storage').select('quantity').eq('product_id', productId).eq('organization_id', context.organizationId),
    ]);
    const inZones = (zoneRows || []).reduce((sum, row) => sum + row.quantity, 0);
    return Math.max(0, (product?.stock_quantity || 0) - inZones);
  }

  /** Pezzi nel totale ma senza zona, per una lista di prodotti (lettura, per gli avvisi prima dello stoccaggio). */
  static async getUnassignedStockForProducts(productIds: string[]): Promise<Map<string, number>> {
    const result = new Map<string, number>();
    if (productIds.length === 0) return result;
    const [{ data: products }, { data: zoneRows }] = await Promise.all([
      supabase.from('products').select('id, stock_quantity').in('id', productIds),
      supabase.from('product_zone_storage').select('product_id, quantity').in('product_id', productIds),
    ]);
    const inZones = new Map<string, number>();
    for (const row of zoneRows || []) {
      inZones.set(row.product_id, (inZones.get(row.product_id) || 0) + row.quantity);
    }
    for (const p of products || []) {
      result.set(p.id, Math.max(0, (p.stock_quantity || 0) - (inZones.get(p.id) || 0)));
    }
    return result;
  }

  /**
   * De-stock products from zones using LIFO (Last In, First Out) strategy
   * @param productId Product to destock
   * @param quantityToDestock Quantity to remove from zones
   * @returns Array of affected zone IDs
   */
  static async destockFromZones(productId: string, quantityToDestock: number): Promise<string[]> {
    if (quantityToDestock <= 0) {
      throw new InvalidQuantityError(quantityToDestock, 'La quantità deve essere maggiore di zero');
    }

    return withTransaction(async (context: TransactionContext) => {
      // Validate stock availability before starting
      await validateStockOperation(productId, -quantityToDestock, context);

      // Get all storage records for this product, ordered by most recent first (LIFO)
      const { data: storageRecords, error: fetchError } = await supabase
        .from('product_zone_storage')
        .select(`
          *,
          zone:warehouse_zones(id, name, code)
        `)
        .eq('organization_id', context.organizationId)
        .eq('product_id', productId)
        .order('stored_at', { ascending: false });

      if (fetchError) throw createWarehouseError(fetchError, 'fetch storage records');

      let remainingToDestock = quantityToDestock;
      const affectedZones = new Set<string>();
      const operationsToExecute: Array<() => Promise<void>> = [];

      // Plan operations first (without executing)
      for (const record of storageRecords || []) {
        if (remainingToDestock <= 0) break;

        const availableQuantity = record.quantity;
        const quantityToRemoveFromThisRecord = Math.min(remainingToDestock, availableQuantity);

        if (quantityToRemoveFromThisRecord === availableQuantity) {
          // Plan: Remove the entire record
          operationsToExecute.push(async () => {
            const { error: deleteError } = await supabase
              .from('product_zone_storage')
              .delete()
              .eq('id', record.id);

            if (deleteError) throw createWarehouseError(deleteError, 'delete storage record');
          });
        } else {
          // Plan: Update the record with reduced quantity
          operationsToExecute.push(async () => {
            const { error: updateError } = await supabase
              .from('product_zone_storage')
              .update({
                quantity: availableQuantity - quantityToRemoveFromThisRecord,
                stored_at: new Date().toISOString() // Update timestamp
              })
              .eq('id', record.id);

            if (updateError) throw createWarehouseError(updateError, 'update storage record');
          });
        }

        remainingToDestock -= quantityToRemoveFromThisRecord;
        affectedZones.add(record.zone_id);
      }

      // Execute all planned storage operations
      for (const operation of operationsToExecute) {
        await operation();
      }

      // Update zone usage for all affected zones
      const affectedZoneIds = Array.from(affectedZones);
      for (const zoneId of affectedZoneIds) {
        await this.updateZoneUsageWithContext(zoneId, context);
      }

      // Log audit trail
      const auditContext: AuditContext = {
        organizationId: context.organizationId,
        userId: context.userId
      };
      
      // Get product name for audit log
      const { data: product } = await supabase
        .from('products')
        .select('name')
        .eq('id', productId)
        .single();

      await AuditTrailService.logZoneDestocked(
        auditContext,
        productId,
        product?.name || 'Unknown Product',
        quantityToDestock,
        affectedZoneIds
      );

      // Aggiorna il totale del prodotto
      await this.applyProductStockDelta(productId, -quantityToDestock, context);

      return affectedZoneIds;
    });
  }

  /**
   * Rettifica assoluta dello stock di un prodotto, coerente con le zone.
   * Imposta stock_quantity = newQuantity, allinea le zone (scarico LIFO se cala,
   * carico sulla zona usata più di recente se sale) e registra UN solo movimento.
   * Entry point unico per: rettifica rapida da lista, campo Quantità del form modifica.
   * @returns delta applicato (0 se nessuna variazione)
   */
  static async adjustProductStock(
    productId: string,
    newQuantity: number,
    reason: string = 'manual_adjustment',
    notes?: string
  ): Promise<number> {
    if (newQuantity < 0) {
      throw new InvalidQuantityError(newQuantity, 'La quantità non può essere negativa');
    }

    return withTransaction(async (context: TransactionContext) => {
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('stock_quantity')
        .eq('id', productId)
        .eq('organization_id', context.organizationId)
        .single();

      if (productError) throw createWarehouseError(productError, 'fetch product stock');

      const previousStock = product?.stock_quantity || 0;
      const delta = newQuantity - previousStock;
      if (delta === 0) return 0;

      // Storage in zone, più recenti prima (LIFO)
      const { data: storages, error: storagesError } = await supabase
        .from('product_zone_storage')
        .select('*')
        .eq('product_id', productId)
        .eq('organization_id', context.organizationId)
        .order('stored_at', { ascending: false });

      if (storagesError) throw createWarehouseError(storagesError, 'fetch zone storage');

      const affectedZones = new Set<string>();

      if (delta < 0) {
        // Scarico LIFO dalle zone (l'eventuale eccedenza non stoccata resta solo globale)
        let remaining = -delta;
        for (const record of storages || []) {
          if (remaining <= 0) break;
          const take = Math.min(remaining, record.quantity);
          if (take <= 0) continue;

          if (take === record.quantity) {
            const { error: deleteError } = await supabase
              .from('product_zone_storage')
              .delete()
              .eq('id', record.id);
            if (deleteError) throw createWarehouseError(deleteError, 'delete storage record');
          } else {
            const { error: updateError } = await supabase
              .from('product_zone_storage')
              .update({
                quantity: record.quantity - take,
                stored_at: new Date().toISOString()
              })
              .eq('id', record.id);
            if (updateError) throw createWarehouseError(updateError, 'update storage record');
          }
          remaining -= take;
          affectedZones.add(record.zone_id);
        }
      } else if (storages && storages.length > 0) {
        // Carico sulla zona usata più di recente
        const target = storages[0];
        const { error: updateError } = await supabase
          .from('product_zone_storage')
          .update({
            quantity: target.quantity + delta,
            stored_at: new Date().toISOString()
          })
          .eq('id', target.id);
        if (updateError) throw createWarehouseError(updateError, 'update storage record');
        affectedZones.add(target.zone_id);
      }

      for (const zoneId of affectedZones) {
        await this.updateZoneUsageWithContext(zoneId, context);
      }

      // Stock assoluto + stato
      const { error: stockError } = await supabase
        .from('products')
        .update({
          stock_quantity: newQuantity,
          stock_status: newQuantity > 0 ? 'instock' : 'outofstock'
        })
        .eq('id', productId);
      if (stockError) throw createWarehouseError(stockError, 'update product stock');

      // Movimento unico per lo storico
      const { error: movementError } = await supabase
        .from('stock_movements')
        .insert([{
          organization_id: context.organizationId,
          product_id: productId,
          type: delta > 0 ? 'in' : 'out',
          quantity: Math.abs(delta),
          previous_stock: previousStock,
          new_stock: newQuantity,
          reason,
          source: 'manual',
          notes: notes || `Rettifica quantità: ${previousStock} → ${newQuantity}`,
          created_by: context.userId
        }]);
      if (movementError) {
        console.error('Error creating stock movement:', movementError);
        // Non bloccare la rettifica se il movimento non viene registrato
      }

      return delta;
    });
  }

  /**
   * Update zone current usage based on stored products
   * @param zoneId Zone ID to update
   */
  static async updateZoneUsage(zoneId: string): Promise<void> {
    return withTransaction(async (context: TransactionContext) => {
      await this.updateZoneUsageWithContext(zoneId, context);
    });
  }

  /**
   * Update zone current usage with provided context (for use within transactions)
   * @param zoneId Zone ID to update
   * @param context Transaction context
   */
  static async updateZoneUsageWithContext(zoneId: string, context: TransactionContext): Promise<void> {
    // Get total quantity stored in this zone
    const { data: storageData, error: storageError } = await supabase
      .from('product_zone_storage')
      .select('quantity')
      .eq('zone_id', zoneId)
      .eq('organization_id', context.organizationId);

    if (storageError) throw createWarehouseError(storageError, 'fetch zone storage');

    const totalQuantity = storageData?.reduce((sum, item) => sum + item.quantity, 0) || 0;

    // Update zone's current_usage
    const { error: updateError } = await supabase
      .from('warehouse_zones')
      .update({ current_usage: totalQuantity })
      .eq('id', zoneId);

    if (updateError) throw createWarehouseError(updateError, 'update zone usage');
  }

  /**
   * Get total quantity of a product stocked in all zones
   * @param productId Product ID
   * @returns Total quantity stocked in zones
   */
  static async getTotalStockedQuantity(productId: string): Promise<number> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      const { data: storageData, error } = await supabase
        .from('product_zone_storage')
        .select('quantity')
        .eq('product_id', productId)
        .eq('organization_id', profile.organization_id);

      if (error) throw error;

      return storageData?.reduce((sum, item) => sum + item.quantity, 0) || 0;

    } catch (error) {
      console.error('Error getting total stocked quantity:', error);
      return 0;
    }
  }

  /**
   * Get all zones where a product is stocked with quantities
   * @param productId Product ID
   * @returns Array of storage records
   */
  static async getProductZoneStorage(productId: string): Promise<ProductZoneStorage[]> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      const { data: storage, error } = await supabase
        .from('product_zone_storage')
        .select(`
          *,
          zone:warehouse_zones(id, name, code, location)
        `)
        .eq('organization_id', profile.organization_id)
        .eq('product_id', productId)
        .order('stored_at', { ascending: false });

      if (error) throw error;

      return storage || [];
    } catch (error) {
      console.error('Error fetching product zone storage:', error);
      return [];
    }
  }

  /**
   * Store products in zone atomically
   * @param productId Product ID
   * @param zoneId Zone ID  
   * @param quantity Quantity to store
   * @param notes Optional notes
   */
  static async storeProductInZone(
    productId: string, 
    zoneId: string, 
    quantity: number, 
    notes?: string,
    options?: { newStock?: boolean }
  ): Promise<void> {
    return withTransaction(async (context: TransactionContext) => {
      // Con newStock i pezzi sono merce nuova (carico); altrimenti si stoccano prima i pezzi già nel totale ma senza zona
      const unassignedBefore = options?.newStock ? 0 : await this.getUnassignedStock(productId, context);
      const stockIncrease = options?.newStock ? quantity : Math.max(0, quantity - unassignedBefore);

      // Check if product is already stored in this zone
      const { data: existingStorage } = await supabase
        .from('product_zone_storage')
        .select('*')
        .eq('product_id', productId)
        .eq('zone_id', zoneId)
        .eq('organization_id', context.organizationId);
        
      const existingRecord = existingStorage && existingStorage.length > 0 ? existingStorage[0] : null;

      if (existingRecord) {
        // Update existing storage
        const { error } = await supabase
          .from('product_zone_storage')
          .update({
            quantity: existingRecord.quantity + quantity,
            notes: notes || existingRecord.notes,
            stored_at: new Date().toISOString()
          })
          .eq('id', existingRecord.id);

        if (error) throw error;
      } else {
        // Create new storage entry
        const { error } = await supabase
          .from('product_zone_storage')
          .insert([{
            organization_id: context.organizationId,
            product_id: productId,
            zone_id: zoneId,
            quantity,
            notes,
            stored_by: context.userId
          }]);

        if (error) throw error;
      }

      // Update zone current usage
      await this.updateZoneUsageWithContext(zoneId, context);

      // Log audit trail
      const auditContext: AuditContext = {
        organizationId: context.organizationId,
        userId: context.userId
      };
      
      // Get product and zone names for audit log
      const [{ data: product }, { data: zone }] = await Promise.all([
        supabase.from('products').select('name').eq('id', productId).single(),
        supabase.from('warehouse_zones').select('name, code').eq('id', zoneId).single()
      ]);

      await AuditTrailService.logProductStored(
        auditContext,
        productId,
        product?.name || 'Unknown Product',
        zoneId,
        zone?.name || 'Unknown Zone',
        quantity,
        notes
      );

      // Recupera lo stock corrente del prodotto per il movimento
      const { data: currentProductData } = await supabase
        .from('products')
        .select('stock_quantity')
        .eq('id', productId)
        .single();

      const previousStock = currentProductData?.stock_quantity || 0;

      let totalUpdatedByMovement = false;
      if (stockIncrease > 0) {
        // Crea movimento di stock per tracciabilità nello storico
        const { error: movementError } = await supabase
          .from('stock_movements')
          .insert([{
            organization_id: context.organizationId,
            product_id: productId,
            type: 'in',
            quantity: stockIncrease,
            previous_stock: previousStock,
            new_stock: previousStock + stockIncrease,
            reason: 'zone_storage',
            source: 'manual',
            zone_id: zoneId,
            notes: notes || `Stoccato in zona: ${formatWarehouseName(zone, 'N/A')}`,
            created_by: context.userId
          }]);

        if (movementError) {
          console.error('Error creating stock movement:', movementError);
          // Non blocchiamo l'operazione se il movimento non viene creato
        } else {
          totalUpdatedByMovement = true;
        }
      }

      // Il trigger del movimento imposta già il totale: si somma a mano solo se il movimento manca
      if (!totalUpdatedByMovement) {
        await this.applyProductStockDelta(productId, stockIncrease, context);
      }
    });
  }

  /**
   * Update existing product zone storage atomically
   * @param storageId Storage record ID
   * @param quantity New quantity
   * @param notes Optional notes
   */
  static async updateProductZoneStorage(
    storageId: string,
    quantity: number,
    notes?: string,
    reason: string = 'zone_adjustment'
  ): Promise<void> {
    return withTransaction(async (context: TransactionContext) => {
      // Get current storage record to find zone_id, product_id and previous quantity
      const { data: currentStorage, error: fetchError } = await supabase
        .from('product_zone_storage')
        .select('zone_id, product_id, quantity')
        .eq('id', storageId)
        .eq('organization_id', context.organizationId)
        .single();

      if (fetchError) throw fetchError;
      if (!currentStorage) throw new Error('Storage record not found');

      const previousZoneQuantity = currentStorage.quantity;
      const delta = quantity - previousZoneQuantity;

      // Update storage record
      const { error: updateError } = await supabase
        .from('product_zone_storage')
        .update({
          quantity,
          notes,
          stored_at: new Date().toISOString()
        })
        .eq('id', storageId);

      if (updateError) throw updateError;

      // Registra un movimento di carico/scarico per tracciabilità nello storico
      // (solo se la quantità è effettivamente cambiata)
      let totalUpdatedByMovement = false;
      if (delta !== 0) {
        const { data: currentProductData } = await supabase
          .from('products')
          .select('stock_quantity')
          .eq('id', currentStorage.product_id)
          .single();

        const previousStock = currentProductData?.stock_quantity || 0;

        const { error: movementError } = await supabase
          .from('stock_movements')
          .insert([{
            organization_id: context.organizationId,
            product_id: currentStorage.product_id,
            type: delta > 0 ? 'in' : 'out',
            quantity: Math.abs(delta),
            previous_stock: previousStock,
            new_stock: previousStock + delta,
            reason,
            source: 'manual',
            zone_id: currentStorage.zone_id,
            notes: notes || undefined,
            created_by: context.userId
          }]);

        if (movementError) {
          console.error('Error creating stock movement:', movementError);
          // Non blocchiamo l'operazione se il movimento non viene creato
        } else {
          totalUpdatedByMovement = true;
        }
      }

      // Update zone usage
      await this.updateZoneUsageWithContext(currentStorage.zone_id, context);

      // Il trigger del movimento imposta già il totale: si somma a mano solo se il movimento manca
      if (!totalUpdatedByMovement) {
        await this.applyProductStockDelta(currentStorage.product_id, delta, context);
      }
    });
  }

  /**
   * Remove product from zone atomically
   * @param storageId Storage record ID
   */
  static async removeProductFromZone(storageId: string): Promise<void> {
    return withTransaction(async (context: TransactionContext) => {
      // Get storage record data before deletion for audit log
      const { data: storageRecord, error: fetchError } = await supabase
        .from('product_zone_storage')
        .select(`
          zone_id, 
          product_id, 
          quantity,
          product:products(name),
          zone:warehouse_zones(name, code)
        `)
        .eq('id', storageId)
        .eq('organization_id', context.organizationId)
        .single() as { data: StorageRecordWithRelations | null; error: any };

      if (fetchError) throw fetchError;
      if (!storageRecord) throw new StorageRecordNotFoundError(storageId);

      // Delete storage record
      const { error: deleteError } = await supabase
        .from('product_zone_storage')
        .delete()
        .eq('id', storageId);

      if (deleteError) throw createWarehouseError(deleteError, 'delete storage record');

      // Update zone usage
      await this.updateZoneUsageWithContext(storageRecord.zone_id, context);

      // Log audit trail
      const auditContext: AuditContext = {
        organizationId: context.organizationId,
        userId: context.userId
      };

      await AuditTrailService.logProductRemoved(
        auditContext,
        storageId,
        storageRecord.product_id,
        storageRecord.product?.name || 'Unknown Product',
        storageRecord.zone_id,
        storageRecord.zone?.name || 'Unknown Zone',
        storageRecord.quantity
      );

      // Movimento di scarico nello storico; il suo trigger imposta già il totale
      let totalUpdatedByMovement = false;
      if (storageRecord.quantity > 0) {
        const { data: currentProductData } = await supabase
          .from('products')
          .select('stock_quantity')
          .eq('id', storageRecord.product_id)
          .eq('organization_id', context.organizationId)
          .single();
        const previousStock = currentProductData?.stock_quantity || 0;
        const { error: movementError } = await supabase
          .from('stock_movements')
          .insert([{
            organization_id: context.organizationId,
            product_id: storageRecord.product_id,
            type: 'out',
            quantity: storageRecord.quantity,
            previous_stock: previousStock,
            new_stock: Math.max(0, previousStock - storageRecord.quantity),
            reason: 'manual_adjustment',
            source: 'manual',
            zone_id: storageRecord.zone_id,
            notes: `Tolto dalla zona: ${formatWarehouseName(storageRecord.zone, 'N/A')}`,
            created_by: context.userId
          }]);
        if (movementError) {
          console.error('Error creating stock movement:', movementError);
        } else {
          totalUpdatedByMovement = true;
        }
      }

      if (!totalUpdatedByMovement) {
        await this.applyProductStockDelta(storageRecord.product_id, -storageRecord.quantity, context);
      }
    });
  }

  /**
   * Fetch zone storage items with optional filtering
   * @param filters Optional filters
   */
  static async fetchZoneStorage(filters?: {
    zoneId?: string;
    productId?: string;
    organizationId?: string;
  }): Promise<ProductZoneStorage[]> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      let query = supabase
        .from('product_zone_storage')
        .select(`
          *,
          product:products(id, name, sku),
          zone:warehouse_zones(id, name, code)
        `)
        .eq('organization_id', filters?.organizationId || profile.organization_id);

      // Apply optional filters
      if (filters?.zoneId) {
        query = query.eq('zone_id', filters.zoneId);
      }
      if (filters?.productId) {
        query = query.eq('product_id', filters.productId);
      }

      const { data: storage, error } = await query.order('stored_at', { ascending: false });

      if (error) throw error;

      return storage || [];
    } catch (error) {
      console.error('Error fetching zone storage:', error);
      throw error;
    }
  }

  /**
   * Fetch zones with optional filtering
   * @param filters Optional filters
   */
  static async fetchZones(filters?: {
    organizationId?: string;
    isActive?: boolean;
  }): Promise<WarehouseZone[]> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      let query = supabase
        .from('warehouse_zones')
        .select('*')
        .eq('organization_id', filters?.organizationId || profile.organization_id);

      // Apply filters
      if (filters?.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive);
      } else {
        query = query.eq('is_active', true); // Default to active zones
      }

      const { data: zones, error } = await query.order('name');

      if (error) throw error;

      return zones || [];
    } catch (error) {
      console.error('Error fetching zones:', error);
      throw error;
    }
  }

  /**
   * Fetch zone by code (for barcode scanning)
   * @param code Zone code
   */
  static async fetchZoneByCode(code: string): Promise<WarehouseZone | null> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      // Genera tutte le varianti del codice (originale, con trattini, con apostrofi)
      const codeWithDash = code.replace(/'/g, '-');
      const codeWithApostrophe = code.replace(/-/g, "'");

      // Crea array di codici unici da cercare
      const codesToSearch = [...new Set([code, codeWithDash, codeWithApostrophe])];

      // Ricerca con tutte le varianti in una sola query
      const { data: zone, error } = await supabase
        .from('warehouse_zones')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .in('code', codesToSearch)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      return zone || null;
    } catch (error) {
      console.error('Error fetching zone by code:', error);
      return null;
    }
  }

  /**
   * Create new zone
   * @param zoneData Zone data
   */
  static async createZone(zoneData: Partial<WarehouseZone>): Promise<WarehouseZone> {
    return withTransaction(async (context: TransactionContext) => {
      const { data: newZone, error } = await supabase
        .from('warehouse_zones')
        .insert([{
          ...zoneData,
          organization_id: context.organizationId
        }])
        .select()
        .single();

      if (error) throw error;

      // Log audit trail
      const auditContext: AuditContext = {
        organizationId: context.organizationId,
        userId: context.userId
      };

      await AuditTrailService.logZoneCreated(
        auditContext,
        newZone.id,
        newZone.name,
        zoneData
      );

      return newZone;
    });
  }

  /**
   * Update zone
   * @param id Zone ID
   * @param updates Update data
   */
  static async updateZone(id: string, updates: Partial<WarehouseZone>): Promise<void> {
    return withTransaction(async (context: TransactionContext) => {
      // Get current zone data for audit log
      const { data: currentZone } = await supabase
        .from('warehouse_zones')
        .select('*')
        .eq('id', id)
        .eq('organization_id', context.organizationId)
        .single();

      const { error } = await supabase
        .from('warehouse_zones')
        .update(updates)
        .eq('id', id)
        .eq('organization_id', context.organizationId);

      if (error) throw error;

      // Log audit trail
      if (currentZone) {
        const auditContext: AuditContext = {
          organizationId: context.organizationId,
          userId: context.userId
        };

        await AuditTrailService.logZoneUpdated(
          auditContext,
          id,
          currentZone.name,
          currentZone,
          { ...currentZone, ...updates }
        );
      }
    });
  }

  /**
   * Delete zone (soft delete)
   * Pulisce automaticamente i record di storage e ricalcola lo stock dei prodotti coinvolti
   * @param id Zone ID
   */
  static async deleteZone(id: string, options?: { skipStockSync?: boolean }): Promise<void> {
    return withTransaction(async (context: TransactionContext) => {
      // Get zone data for audit log
      const { data: zone } = await supabase
        .from('warehouse_zones')
        .select('name')
        .eq('id', id)
        .eq('organization_id', context.organizationId)
        .single();

      // 1. Trova tutti i prodotti stoccati in questa zona PRIMA di eliminarli
      const { data: storageRecords } = await supabase
        .from('product_zone_storage')
        .select('product_id, quantity')
        .eq('zone_id', id)
        .eq('organization_id', context.organizationId);

      // 2. Elimina i record di storage associati alla zona
      if (storageRecords && storageRecords.length > 0) {
        const { error: deleteStorageError } = await supabase
          .from('product_zone_storage')
          .delete()
          .eq('zone_id', id)
          .eq('organization_id', context.organizationId);

        if (deleteStorageError) {
          console.error('Error deleting zone storage records:', deleteStorageError);
          throw createWarehouseError(deleteStorageError, 'delete zone storage');
        }

        // 3. Toglie dal totale i pezzi che erano in questa zona (skip se richiesto)
        if (!options?.skipStockSync) {
          const removedByProduct = new Map<string, number>();
          for (const r of storageRecords) {
            removedByProduct.set(r.product_id, (removedByProduct.get(r.product_id) || 0) + r.quantity);
          }
          for (const [productId, removed] of removedByProduct) {
            await this.applyProductStockDelta(productId, -removed, context);
          }
        }
      }

      // 4. Soft delete della zona
      const { error } = await supabase
        .from('warehouse_zones')
        .update({ is_active: false })
        .eq('id', id)
        .eq('organization_id', context.organizationId);

      if (error) throw error;

      // Log audit trail
      const auditContext: AuditContext = {
        organizationId: context.organizationId,
        userId: context.userId
      };

      await AuditTrailService.logZoneDeleted(
        auditContext,
        id,
        zone?.name || 'Unknown Zone'
      );
    });
  }

  /**
   * BULK OPERATIONS for better performance
   */

  /**
   * Bulk store products in zones with single transaction
   */
  static async bulkStoreProducts(
    operations: Array<{
      productId: string;
      zoneId: string;
      quantity: number;
      notes?: string;
    }>
  ): Promise<void> {
    return withTransaction(async (context: TransactionContext) => {
      const auditContext: AuditContext = {
        organizationId: context.organizationId,
        userId: context.userId
      };

      // Prima si stoccano i pezzi già nel totale ma senza zona; solo l'eccedenza è merce nuova
      const unassignedByProduct = new Map<string, number>();
      for (const productId of new Set(operations.map(op => op.productId))) {
        unassignedByProduct.set(productId, await this.getUnassignedStock(productId, context));
      }
      const increaseByProduct = new Map<string, number>();
      for (const op of operations) {
        const unassigned = unassignedByProduct.get(op.productId) || 0;
        const fromUnassigned = Math.min(unassigned, op.quantity);
        unassignedByProduct.set(op.productId, unassigned - fromUnassigned);
        increaseByProduct.set(op.productId, (increaseByProduct.get(op.productId) || 0) + (op.quantity - fromUnassigned));
      }

      // Group by zone for efficiency
      const zoneGroups = new Map<string, typeof operations>();
      for (const op of operations) {
        if (!zoneGroups.has(op.zoneId)) {
          zoneGroups.set(op.zoneId, []);
        }
        zoneGroups.get(op.zoneId)!.push(op);
      }

      // Prepare bulk storage records
      const storageRecords: ProductZoneStorageInsert[] = [];
      const auditPromises: Promise<void>[] = [];

      for (const [zoneId, zoneOps] of zoneGroups) {
        for (const op of zoneOps) {
          // Check for existing storage
          const { data: existing } = await supabase
            .from('product_zone_storage')
            .select('id, quantity')
            .eq('product_id', op.productId)
            .eq('zone_id', op.zoneId)
            .eq('organization_id', context.organizationId)
            .single();

          if (existing) {
            // Update existing record
            await supabase
              .from('product_zone_storage')
              .update({
                quantity: existing.quantity + op.quantity,
                stored_at: new Date().toISOString(),
                notes: op.notes || null
              })
              .eq('id', existing.id);
          } else {
            // Prepare for bulk insert
            storageRecords.push({
              organization_id: context.organizationId,
              product_id: op.productId,
              zone_id: op.zoneId,
              quantity: op.quantity,
              notes: op.notes,
              stored_by: context.userId
            });
          }

          // Prepare audit log (we'll do this in bulk later)
          const productPromise = supabase
            .from('products')
            .select('name')
            .eq('id', op.productId)
            .single();
          const zonePromise = supabase
            .from('warehouse_zones')
            .select('name')
            .eq('id', op.zoneId)
            .single();

          auditPromises.push(
            Promise.all([productPromise, zonePromise]).then(([productRes, zoneRes]) =>
              AuditTrailService.logProductStored(
                auditContext,
                op.productId,
                productRes.data?.name || 'Unknown Product',
                op.zoneId,
                zoneRes.data?.name || 'Unknown Zone',
                op.quantity,
                op.notes
              )
            )
          );
        }
      }

      // Bulk insert new storage records
      if (storageRecords.length > 0) {
        await bulkInsert('product_zone_storage', storageRecords);
      }

      // Update zone usage for all affected zones
      const affectedZones = Array.from(zoneGroups.keys());
      const usagePromises = affectedZones.map(zoneId =>
        this.updateZoneUsageWithContext(zoneId, context)
      );
      await Promise.all(usagePromises);

      // Execute audit logs in parallel
      await Promise.all(auditPromises);

      // Aggiorna il totale di tutti i prodotti coinvolti
      for (const [productId, added] of increaseByProduct) {
        await this.applyProductStockDelta(productId, added, context);
      }
    });
  }

  /**
   * Bulk update zone usage for multiple zones efficiently
   */
  static async bulkUpdateZoneUsage(zoneIds: string[]): Promise<void> {
    return withTransaction(async (context: TransactionContext) => {
      // Get usage for all zones in single query
      const { data: usageData } = await supabase
        .from('product_zone_storage')
        .select('zone_id, quantity')
        .in('zone_id', zoneIds)
        .eq('organization_id', context.organizationId);

      // Calculate usage per zone
      const zoneUsage = new Map<string, number>();
      for (const zoneId of zoneIds) {
        zoneUsage.set(zoneId, 0);
      }

      if (usageData) {
        for (const item of usageData) {
          const current = zoneUsage.get(item.zone_id) || 0;
          zoneUsage.set(item.zone_id, current + item.quantity);
        }
      }

      // Prepare bulk updates
      const updates = Array.from(zoneUsage.entries()).map(([zoneId, usage]) => ({
        id: zoneId,
        data: { current_usage: usage }
      }));

      // Execute bulk update
      await bulkUpdate('warehouse_zones', updates);
    });
  }

  /**
   * Bulk destock products from zones with optimized queries
   */
  static async bulkDestockProducts(
    operations: Array<{
      productId: string;
      quantity: number;
    }>
  ): Promise<Record<string, string[]>> {
    const results: Record<string, string[]> = {};

    await withTransaction(async (context: TransactionContext) => {
      const auditContext: AuditContext = {
        organizationId: context.organizationId,
        userId: context.userId
      };

      // Validate all operations first
      for (const op of operations) {
        await validateStockOperation(op.productId, -op.quantity, context);
      }

      // Process each product
      for (const op of operations) {
        const affectedZones = await this.destockFromZones(op.productId, op.quantity);
        results[op.productId] = affectedZones;
      }
    });

    return results;
  }

  /**
   * Optimized fetch with selective field loading and pagination
   */
  static async fetchZoneStorageOptimized(filters?: {
    zoneId?: string;
    productId?: string;
    organizationId?: string;
    page?: number;
    limit?: number;
    searchTerm?: string;
  }): Promise<{
    data: ProductZoneStorage[];
    count: number;
    hasMore: boolean;
  }> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new UnauthorizedOperationError('fetch storage', 'User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new UnauthorizedOperationError('fetch storage', 'User not associated with an organization');
      }

      // Use optimized query builder
      let baseQuery = QueryOptimizer.buildSelectiveQuery('product_zone_storage', {
        basic: ['id', 'product_id', 'zone_id', 'quantity', 'stored_at', 'notes'],
        relations: {
          'product': ['id', 'name', 'sku'],
          'zone': ['id', 'name', 'code']
        }
      }).eq('organization_id', filters?.organizationId || profile.organization_id);

      // Apply filters
      if (filters?.zoneId) {
        baseQuery = baseQuery.eq('zone_id', filters.zoneId);
      }
      if (filters?.productId) {
        baseQuery = baseQuery.eq('product_id', filters.productId);
      }

      // Apply pagination and search
      const query = QueryOptimizer.buildPaginatedQuery(baseQuery, {
        page: filters?.page,
        limit: filters?.limit,
        orderBy: 'stored_at',
        orderDirection: 'desc',
        searchColumns: ['product.name', 'product.sku', 'zone.name'],
        searchTerm: filters?.searchTerm
      });

      const { data: storage, error, count } = await query;

      if (error) throw error;

      const limit = filters?.limit || 50;
      const hasMore = (count || 0) > (filters?.page || 1) * limit;

      return {
        data: storage || [],
        count: count || 0,
        hasMore
      };

    } catch (error) {
      console.error('Error fetching optimized zone storage:', error);
      throw createWarehouseError(error, 'fetch zone storage');
    }
  }

  /**
   * Optimized zones fetch with caching strategy
   */
  static async fetchZonesOptimized(filters?: {
    organizationId?: string;
    isActive?: boolean;
    includeUsage?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{
    data: WarehouseZoneWithUsage[];
    count: number;
    hasMore: boolean;
  }> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new UnauthorizedOperationError('fetch zones', 'User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new UnauthorizedOperationError('fetch zones', 'User not associated with an organization');
      }

      // Build selective query
      const fields = {
        basic: ['id', 'name', 'code', 'description', 'location', 'capacity_limit', 'current_usage', 'is_active', 'created_at'],
        relations: filters?.includeUsage ? {
          'storage_items:product_zone_storage': ['quantity']
        } : undefined
      };

      let baseQuery = QueryOptimizer.buildSelectiveQuery('warehouse_zones', fields)
        .eq('organization_id', filters?.organizationId || profile.organization_id);

      // Apply filters
      if (filters?.isActive !== undefined) {
        baseQuery = baseQuery.eq('is_active', filters.isActive);
      } else {
        baseQuery = baseQuery.eq('is_active', true); // Default to active zones
      }

      // Apply pagination
      const query = QueryOptimizer.buildPaginatedQuery(baseQuery, {
        page: filters?.page,
        limit: filters?.limit,
        orderBy: 'name',
        orderDirection: 'asc'
      });

      const { data: zones, error, count } = await query;

      if (error) throw error;

      const limit = filters?.limit || 50;
      const hasMore = (count || 0) > (filters?.page || 1) * limit;

      return {
        data: zones || [],
        count: count || 0,
        hasMore
      };

    } catch (error) {
      console.error('Error fetching optimized zones:', error);
      throw createWarehouseError(error, 'fetch zones');
    }
  }

  /**
   * STOCK RESERVATION INTEGRATION METHODS
   */

  /**
   * Store product in zone with automatic reservation fulfillment
   */
  static async storeProductWithReservationCheck(
    productId: string,
    zoneId: string,
    quantity: number,
    notes?: string
  ): Promise<{ storedQuantity: number; reservationsFulfilled: string[] }> {
    return withTransaction(async (context: TransactionContext) => {
      const auditContext: AuditContext = {
        organizationId: context.organizationId,
        userId: context.userId
      };

      // First, store the product
      await this.storeProductInZone(productId, zoneId, quantity, notes);

      // Check for pending reservations that can be fulfilled
      const { reservations } = await StockReservationService.getReservations(
        context.organizationId,
        {
          productId,
          zoneId,
          status: ['active'],
          limit: 10 // Check first 10 reservations
        }
      );

      const reservationsFulfilled: string[] = [];
      let quantityUsedForReservations = 0;

      // Try to fulfill reservations by priority (highest first)
      const sortedReservations = reservations
        .sort((a, b) => b.priority - a.priority)
        .sort((a, b) => new Date(a.reserved_at).getTime() - new Date(b.reserved_at).getTime());

      for (const reservation of sortedReservations) {
        if (quantityUsedForReservations + reservation.quantity <= quantity) {
          // We can fulfill this reservation
          await StockReservationService.fulfillReservation(
            reservation.id,
            reservation.quantity,
            auditContext
          );
          
          reservationsFulfilled.push(reservation.id);
          quantityUsedForReservations += reservation.quantity;
        }
      }

      return {
        storedQuantity: quantity,
        reservationsFulfilled
      };
    });
  }

  /**
   * Check if a destock operation would conflict with reservations
   */
  static async checkDestockReservationConflict(
    productId: string,
    requestedQuantity: number
  ): Promise<{
    canDestock: boolean;
    availableQuantity: number;
    conflictingReservations: any[];
    suggestedAction?: string;
  }> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      // Get all zones with this product
      const { data: zoneStorage } = await supabase
        .from('product_zone_storage')
        .select(`
          zone_id,
          quantity,
          zone:warehouse_zones!zone_id(name, current_usage, reserved_quantity)
        `)
        .eq('product_id', productId)
        .eq('organization_id', profile.organization_id)
        .order('quantity', { ascending: false }); // LIFO order

      if (!zoneStorage || zoneStorage.length === 0) {
        return {
          canDestock: false,
          availableQuantity: 0,
          conflictingReservations: [],
          suggestedAction: 'No stock available for this product'
        };
      }

      // Get active reservations for this product
      const { reservations: activeReservations } = await StockReservationService.getReservations(
        profile.organization_id,
        {
          productId,
          status: ['active']
        }
      );

      // Calculate total available (non-reserved) stock
      let totalAvailableStock = 0;
      for (const storage of zoneStorage) {
        const reservedInZone = activeReservations
          .filter(r => r.zone_id === storage.zone_id)
          .reduce((sum, r) => sum + r.quantity, 0);
        
        const availableInZone = Math.max(0, storage.quantity - reservedInZone);
        totalAvailableStock += availableInZone;
      }

      const canDestock = totalAvailableStock >= requestedQuantity;
      const conflictingReservations = canDestock ? [] : activeReservations;

      let suggestedAction: string | undefined;
      if (!canDestock) {
        if (activeReservations.length > 0) {
          suggestedAction = `Consider cancelling some reservations. ${activeReservations.length} active reservations found.`;
        } else {
          suggestedAction = 'Insufficient stock available.';
        }
      }

      return {
        canDestock,
        availableQuantity: totalAvailableStock,
        conflictingReservations,
        suggestedAction
      };
    } catch (error) {
      console.error('Error checking destock reservation conflict:', error);
      throw createWarehouseError(error, 'check destock reservation conflict');
    }
  }

  /**
   * Destock with reservation awareness - respects active reservations
   */
  static async destockWithReservationAwareness(
    productId: string,
    quantityToDestock: number,
    ignoreReservations: boolean = false
  ): Promise<{
    affectedZones: string[];
    destockedQuantity: number;
    reservationConflicts?: any[];
  }> {
    return withTransaction(async (context: TransactionContext) => {
      if (!ignoreReservations) {
        // Check for reservation conflicts first
        const conflictCheck = await this.checkDestockReservationConflict(productId, quantityToDestock);
        
        if (!conflictCheck.canDestock) {
          throw new Error(
            `Cannot destock ${quantityToDestock} units. Only ${conflictCheck.availableQuantity} units available (${conflictCheck.conflictingReservations.length} active reservations). ${conflictCheck.suggestedAction}`
          );
        }
      }

      // Proceed with normal LIFO destock
      const affectedZones = await this.destockFromZones(productId, quantityToDestock);
      
      return {
        affectedZones,
        destockedQuantity: quantityToDestock,
        reservationConflicts: []
      };
    });
  }

  /**
   * Get comprehensive product availability including reservations
   */
  static async getProductAvailabilityWithReservations(
    productId: string
  ): Promise<{
    totalStock: number;
    totalReserved: number;
    availableStock: number;
    zoneBreakdown: Array<{
      zoneId: string;
      zoneName: string;
      totalStock: number;
      reservedStock: number;
      availableStock: number;
      activeReservations: number;
    }>;
  }> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      // Get stock by zone
      const { data: zoneStorage } = await supabase
        .from('product_zone_storage')
        .select(`
          zone_id,
          quantity,
          zone:warehouse_zones!zone_id(id, name, code)
        `)
        .eq('product_id', productId)
        .eq('organization_id', profile.organization_id);

      // Get reservations by zone
      const { reservations } = await StockReservationService.getReservations(
        profile.organization_id,
        {
          productId,
          status: ['active']
        }
      );

      let totalStock = 0;
      let totalReserved = 0;
      const zoneBreakdown = [];

      for (const storage of zoneStorage || []) {
        const zoneReservations = reservations.filter(r => r.zone_id === storage.zone_id);
        const reservedInZone = zoneReservations.reduce((sum, r) => sum + r.quantity, 0);
        const availableInZone = Math.max(0, storage.quantity - reservedInZone);

        totalStock += storage.quantity;
        totalReserved += reservedInZone;

        zoneBreakdown.push({
          zoneId: storage.zone_id,
          // relazione molti-a-uno: a runtime è un oggetto, i tipi generati la vedono come array
          zoneName: formatWarehouseName(storage.zone as unknown as { name?: string; code?: string } | null, 'Unknown Zone'),
          totalStock: storage.quantity,
          reservedStock: reservedInZone,
          availableStock: availableInZone,
          activeReservations: zoneReservations.length
        });
      }

      return {
        totalStock,
        totalReserved,
        availableStock: totalStock - totalReserved,
        zoneBreakdown
      };
    } catch (error) {
      console.error('Error getting product availability with reservations:', error);
      throw createWarehouseError(error, 'get product availability with reservations');
    }
  }

  /**
   * Suggest optimal zone for new reservation based on stock and existing reservations
   */
  static async suggestOptimalZoneForReservation(
    productId: string,
    requestedQuantity: number
  ): Promise<{
    suggestedZone?: {
      zoneId: string;
      zoneName: string;
      availableQuantity: number;
      reason: string;
    };
    alternatives: Array<{
      zoneId: string;
      zoneName: string;
      availableQuantity: number;
      note: string;
    }>;
  }> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      const availability = await this.getProductAvailabilityWithReservations(productId);
      
      // Find zones that can fulfill the full request
      const suitableZones = availability.zoneBreakdown
        .filter(zone => zone.availableStock >= requestedQuantity)
        .sort((a, b) => {
          // Prefer zones with exact match, then zones with less waste
          const aWaste = a.availableStock - requestedQuantity;
          const bWaste = b.availableStock - requestedQuantity;
          if (aWaste === 0 && bWaste !== 0) return -1;
          if (bWaste === 0 && aWaste !== 0) return 1;
          return aWaste - bWaste;
        });

      // Find partial alternatives
      const alternatives = availability.zoneBreakdown
        .filter(zone => zone.availableStock > 0 && zone.availableStock < requestedQuantity)
        .sort((a, b) => b.availableStock - a.availableStock)
        .map(zone => ({
          zoneId: zone.zoneId,
          zoneName: zone.zoneName,
          availableQuantity: zone.availableStock,
          note: `Can provide ${zone.availableStock}/${requestedQuantity} units`
        }));

      const suggestedZone = suitableZones[0] ? {
        zoneId: suitableZones[0].zoneId,
        zoneName: suitableZones[0].zoneName,
        availableQuantity: suitableZones[0].availableStock,
        reason: suitableZones[0].availableStock === requestedQuantity 
          ? 'Exact match available'
          : `Has ${suitableZones[0].availableStock} units available with minimal waste`
      } : undefined;

      return {
        suggestedZone,
        alternatives: alternatives.slice(0, 5) // Top 5 alternatives
      };
    } catch (error) {
      console.error('Error suggesting optimal zone for reservation:', error);
      throw createWarehouseError(error, 'suggest optimal zone for reservation');
    }
  }

  /**
   * Transfer products between zones
   * @param productId Product ID
   * @param sourceZoneId Source zone ID
   * @param destinationZoneId Destination zone ID
   * @param quantity Quantity to transfer
   * @param notes Optional notes
   */
  static async transferBetweenZones(
    productId: string,
    sourceZoneId: string,
    destinationZoneId: string,
    quantity: number,
    notes?: string
  ): Promise<void> {
    if (quantity <= 0) {
      throw new InvalidQuantityError(quantity, 'La quantità deve essere maggiore di zero');
    }

    if (sourceZoneId === destinationZoneId) {
      throw new Error('La zona di origine e destinazione devono essere diverse');
    }

    return withTransaction(async (context: TransactionContext) => {
      // 1. Verifica che il prodotto sia presente nella zona origine con quantità sufficiente
      const { data: sourceStorage, error: sourceError } = await supabase
        .from('product_zone_storage')
        .select('*')
        .eq('product_id', productId)
        .eq('zone_id', sourceZoneId)
        .eq('organization_id', context.organizationId)
        .single();

      if (sourceError || !sourceStorage) {
        throw new StorageRecordNotFoundError(`Prodotto non trovato nella zona origine`);
      }

      if (sourceStorage.quantity < quantity) {
        throw new InvalidQuantityError(
          quantity,
          `Quantità insufficiente nella zona origine. Disponibili: ${sourceStorage.quantity}`
        );
      }

      // 2. Get zone names for logging
      const [{ data: sourceZone }, { data: destZone }, { data: product }] = await Promise.all([
        supabase.from('warehouse_zones').select('name, code').eq('id', sourceZoneId).single(),
        supabase.from('warehouse_zones').select('name, code').eq('id', destinationZoneId).single(),
        supabase.from('products').select('name, stock_quantity').eq('id', productId).single()
      ]);

      // 3. Aggiorna/rimuovi dalla zona origine
      const newSourceQuantity = sourceStorage.quantity - quantity;
      if (newSourceQuantity <= 0) {
        // Rimuovi il record se la quantità diventa 0
        const { error: deleteError } = await supabase
          .from('product_zone_storage')
          .delete()
          .eq('id', sourceStorage.id);

        if (deleteError) throw createWarehouseError(deleteError, 'delete source storage');
      } else {
        // Aggiorna la quantità
        const { error: updateError } = await supabase
          .from('product_zone_storage')
          .update({ quantity: newSourceQuantity, stored_at: new Date().toISOString() })
          .eq('id', sourceStorage.id);

        if (updateError) throw createWarehouseError(updateError, 'update source storage');
      }

      // 4. Aggiorna/crea nella zona destinazione
      const { data: destStorage } = await supabase
        .from('product_zone_storage')
        .select('*')
        .eq('product_id', productId)
        .eq('zone_id', destinationZoneId)
        .eq('organization_id', context.organizationId)
        .single();

      if (destStorage) {
        // Aggiorna quantità esistente
        const { error: updateError } = await supabase
          .from('product_zone_storage')
          .update({
            quantity: destStorage.quantity + quantity,
            stored_at: new Date().toISOString()
          })
          .eq('id', destStorage.id);

        if (updateError) throw createWarehouseError(updateError, 'update destination storage');
      } else {
        // Crea nuovo record
        const { error: insertError } = await supabase
          .from('product_zone_storage')
          .insert([{
            organization_id: context.organizationId,
            product_id: productId,
            zone_id: destinationZoneId,
            quantity: quantity,
            notes: notes,
            stored_by: context.userId
          }]);

        if (insertError) throw createWarehouseError(insertError, 'insert destination storage');
      }

      // 5. Aggiorna usage di entrambe le zone
      await Promise.all([
        this.updateZoneUsageWithContext(sourceZoneId, context),
        this.updateZoneUsageWithContext(destinationZoneId, context)
      ]);

      // 6. Crea movimento di stock per tracciabilità
      const transferNotes = notes || `Trasferimento da ${formatWarehouseName(sourceZone, 'N/A')} a ${formatWarehouseName(destZone, 'N/A')}`;
      const currentStock = product?.stock_quantity || 0;

      const { error: movementError } = await supabase
        .from('stock_movements')
        .insert([{
          organization_id: context.organizationId,
          product_id: productId,
          type: 'transfer',
          quantity: quantity,
          previous_stock: currentStock,
          new_stock: currentStock, // Lo stock totale non cambia nel trasferimento
          reason: 'zone_transfer',
          source: 'manual',
          zone_id: destinationZoneId, // Zona destinazione
          notes: transferNotes,
          created_by: context.userId
        }]);

      if (movementError) {
        console.error('Error creating transfer movement:', movementError);
      }

      // 7. Log audit trail
      const auditContext: AuditContext = {
        organizationId: context.organizationId,
        userId: context.userId
      };

      await AuditTrailService.logZoneTransfer(
        auditContext,
        productId,
        product?.name || 'Unknown Product',
        sourceZoneId,
        sourceZone?.name || 'Unknown Zone',
        destinationZoneId,
        destZone?.name || 'Unknown Zone',
        quantity,
        notes
      );
    });
  }
}